import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange.bind(this), 300);
  #debouncedSectionRefresh = debounce(this.#refreshSection.bind(this), 2000);
  #checkoutPending = false;
  #checkoutRecoveryTimer;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.cartAdd, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener('click', this.#handleCheckoutClick, true);
    document.addEventListener('submit', this.#handleCheckoutSubmit, true);
    document.addEventListener('primary-click-error', this.#resetCheckout);
    document.addEventListener('secondary-click', this.#handleSecondaryCheckoutComplete);
    window.addEventListener('pageshow', this.#resetCheckout);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.cartAdd, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener('click', this.#handleCheckoutClick, true);
    document.removeEventListener('submit', this.#handleCheckoutSubmit, true);
    document.removeEventListener('primary-click-error', this.#resetCheckout);
    document.removeEventListener('secondary-click', this.#handleSecondaryCheckoutComplete);
    window.removeEventListener('pageshow', this.#resetCheckout);
    this.#debouncedOnChange.cancel();
    this.#debouncedSectionRefresh.cancel();
    window.clearTimeout(this.#checkoutRecoveryTimer);
  }

  /**
   * Starts the pending state for Checkout+'s two checkout actions.
   * Its info and protection controls are intentionally excluded.
   * @param {MouseEvent} event
   */
  #handleCheckoutClick = (event) => {
    const path = event.composedPath();
    if (!path.includes(this)) return;

    const isNativeCheckout = path.some(
      (element) => element instanceof HTMLButtonElement && element.matches('button[name="checkout"]')
    );
    const isCheckoutPlus = path.some(
      (element) => element instanceof HTMLElement && element.matches('checkout-plus, [is="checkout-plus"]')
    );
    const isCheckoutPlusAction = path.some(
      (element) => element instanceof HTMLButtonElement && ['primary-button', 'secondary-button'].includes(element.id)
    );

    if (!isNativeCheckout && !(isCheckoutPlus && isCheckoutPlusAction)) return;

    if (this.#checkoutPending) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    this.#startCheckout();
  };

  /**
   * Starts the pending state when the native cart form is submitted.
   * @param {SubmitEvent} event
   */
  #handleCheckoutSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement) || !event.target.matches('.cart-form')) return;
    if (!(event.submitter instanceof HTMLButtonElement) || event.submitter.name !== 'checkout') return;
    if (!this.contains(event.submitter)) return;

    this.#startCheckout();
  };

  /**
   * Checkout+ emits this event after its secondary action finishes, whether it
   * navigated or failed. Give successful navigation time to unload the page;
   * if it does not, restore the cart before the general recovery timeout.
   * @param {Event} event
   */
  #handleSecondaryCheckoutComplete = (event) => {
    if (!this.#checkoutPending || !event.composedPath().includes(this)) return;

    window.clearTimeout(this.#checkoutRecoveryTimer);
    this.#checkoutRecoveryTimer = window.setTimeout(this.#resetCheckout, 8000);
  };

  #startCheckout() {
    if (this.#checkoutPending) return;

    this.#checkoutPending = true;
    this.#debouncedSectionRefresh.cancel();
    this.#disableCartItems();
    this.toggleAttribute('data-checkout-pending', true);
    this.setAttribute('aria-busy', 'true');

    const checkoutButton = this.querySelector('button[name="checkout"]');
    checkoutButton?.setAttribute('aria-disabled', 'true');

    const checkoutLabel = checkoutButton?.querySelector('.cart__checkout-button-label')?.textContent?.trim();
    const checkoutStatus = this.querySelector('[data-checkout-status]');
    if (checkoutStatus && checkoutLabel) checkoutStatus.textContent = `${checkoutLabel}\u2026`;

    window.clearTimeout(this.#checkoutRecoveryTimer);
    this.#checkoutRecoveryTimer = window.setTimeout(this.#resetCheckout, 15000);
  }

  #resetCheckout = (event) => {
    if (!this.#checkoutPending) return;

    const shouldRefresh =
      !event || event.type.endsWith('-click-error') || (event.type === 'pageshow' && event.persisted);

    this.#checkoutPending = false;
    window.clearTimeout(this.#checkoutRecoveryTimer);
    this.#enableCartItems();
    this.toggleAttribute('data-checkout-pending', false);
    this.removeAttribute('aria-busy');

    this.querySelector('button[name="checkout"]')?.removeAttribute('aria-disabled');

    const checkoutStatus = this.querySelector('[data-checkout-status]');
    if (checkoutStatus) checkoutStatus.textContent = '';

    if (shouldRefresh) this.#refreshSection();
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          if (this.#checkoutPending) return;

          this.#handleCartError(line, parsedResponseText);
          return;
        }

        // Preserve a quantity write that was already queued before checkout,
        // but do not redraw the cart while Checkout+ is preparing navigation.
        if (this.#checkoutPending) return;

        const sectionHtml = parsedResponseText.sections[this.sectionId];

        if (config.quantity !== 0 && this.#isTransientEmptyCartHtml(sectionHtml)) {
          this.#debouncedSectionRefresh();
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, sectionHtml);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (!this.#checkoutPending) this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (this.#checkoutPending) return;

    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (!(event instanceof CartUpdateEvent) && !(event instanceof CartAddEvent)) return;
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      if (this.#isTransientEmptyCartHtml(cartItemsHtml)) {
        this.#debouncedSectionRefresh();
        return;
      }

      this.#debouncedSectionRefresh.cancel();
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      // Installed cart apps can emit several section-less updates while their
      // related cart writes are still racing. Coalesce those events so an
      // intermediate empty response never replaces the visible cart.
      this.#debouncedSectionRefresh();
    }
  };

  #refreshSection() {
    sectionRenderer.renderSection(this.sectionId, { cache: false });
  }

  /**
   * GWP and checkout apps can emit an empty cart section while they rewrite
   * line items. Ignore that snapshot when the drawer still has items.
   * @param {string} html
   * @returns {boolean}
   */
  #isTransientEmptyCartHtml(html) {
    if (!html || !this.querySelector('tbody .cart-items__table-row')) return false;

    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    return Boolean(parsedHtml.querySelector('.cart-items__empty-button')) &&
      !parsedHtml.querySelector('tbody .cart-items__table-row');
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
